import { Component, EventEmitter, Input, Output } from '@angular/core';

interface VwDatePreset {
  key: string;
  label: string;
  range(): { from: string; to: string };
}

// Drop-in replacement for a report's two raw <input type="date"> fields —
// same [(ngModel)]-style two-way binding ([(fromDate)]/[(toDate)]) the
// existing "startDate"/"endDate" component properties already use, just
// adding Today/This Week/This Month shortcut buttons above the fields so a
// user doesn't have to pick both ends of a common range by hand every time.
@Component({
  selector: 'app-date-range-preset',
  templateUrl: 'date-range-preset.component.html',
  styleUrls: ['date-range-preset.component.scss']
})
export class DateRangePresetComponent {
  @Input() fromLabel = 'From Date';
  @Input() toLabel = 'End Date';
  @Input() fromDate: string;
  @Output() fromDateChange = new EventEmitter<string>();
  @Input() toDate: string;
  @Output() toDateChange = new EventEmitter<string>();

  // Visitor records can't exist in the future, so neither end of the range
  // should be pickable past today — without this the native date picker
  // happily lets someone pick a future end date and the report just comes
  // back empty with no indication why.
  readonly maxDate = this.fmt(new Date());

  readonly presets: VwDatePreset[] = [
    { key: 'today', label: 'Today', range: () => { const d = this.fmt(new Date()); return { from: d, to: d }; } },
    { key: 'week', label: 'This Week', range: () => this.weekRange() },
    { key: 'month', label: 'This Month', range: () => this.monthRange() },
  ];

  applyPreset(preset: VwDatePreset): void {
    const { from, to } = preset.range();
    this.fromDate = from;
    this.toDate = to;
    this.fromDateChange.emit(from);
    this.toDateChange.emit(to);
  }

  onFromInput(value: string): void {
    this.fromDate = value;
    this.fromDateChange.emit(value);
  }

  onToInput(value: string): void {
    this.toDate = value;
    this.toDateChange.emit(value);
  }

  private fmt(d: Date): string {
    const y = d.getFullYear();
    const m = ('' + (d.getMonth() + 1)).padStart(2, '0');
    const day = ('' + d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private weekRange(): { from: string; to: string } {
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - now.getDay());
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { from: this.fmt(start), to: this.fmt(end) };
  }

  private monthRange(): { from: string; to: string } {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { from: this.fmt(start), to: this.fmt(end) };
  }
}
